export type MiaolinTaxIdLookupResult =
  | {
      status: 'found'
      taxId: string
      companyTitle: string
      providerMode: MiaolinTaxIdLookupProviderMode
      sourceLabel: string
    }
  | {
      status: 'invalid' | 'not_found'
      taxId: string
      message: string
      providerMode: MiaolinTaxIdLookupProviderMode
      sourceLabel: string
    }

export type MiaolinTaxIdLookupProviderMode = 'synthetic' | 'gcis_open_data'

export const MIAOLIN_TAX_ID_LOOKUP_SOURCE_LABEL = '財政部營業登記資料 mock-safe preview'
export const GCIS_TAX_ID_LOOKUP_SOURCE_LABEL = '財政部商工登記公示資料查詢服務'
export const GCIS_TAX_ID_LOOKUP_ENDPOINT =
  'https://data.gcis.nat.gov.tw/od/data/api/5F64D864-61CB-4D0D-8AD9-492047CC1EA6'
export const MIAOLIN_TAX_ID_LOOKUP_PROXY_PATH = '/api/public/tax-id-lookup'

type TaxIdLookupFetchResponse = {
  ok: boolean
  json: () => Promise<unknown>
}

type TaxIdLookupFetcher = (input: string, init?: RequestInit) => Promise<TaxIdLookupFetchResponse>

type TaxIdLookupOptions = {
  fetcher?: TaxIdLookupFetcher
  signal?: AbortSignal
}

const syntheticCompanyTitles = new Map([
  ['01234567', '零一二三測試股份有限公司'],
  ['24591234', '苗林測試食品股份有限公司'],
  ['53789651', '苗林實業股份有限公司'],
  ['90337712', '苗林既有客戶測試有限公司'],
])

export function normalizeMiaolinTaxIdInput(value: string) {
  return value.replace(/[^\d]/g, '').slice(0, 8)
}

export function buildGcisTaxIdLookupUrl(taxId: string) {
  const filter = encodeURIComponent(`Business_Accounting_NO eq ${taxId}`)
  return `${GCIS_TAX_ID_LOOKUP_ENDPOINT}?$format=json&$filter=${filter}`
}

export function buildGcisTaxIdLookupProxyUrl(taxId: string) {
  return `${MIAOLIN_TAX_ID_LOOKUP_PROXY_PATH}?taxId=${encodeURIComponent(taxId)}`
}

export function lookupSyntheticMiaolinTaxIdCompanyTitle(
  rawValue: string,
): MiaolinTaxIdLookupResult {
  const taxId = normalizeMiaolinTaxIdInput(rawValue)
  const base = {
    taxId,
    providerMode: 'synthetic' as const,
    sourceLabel: MIAOLIN_TAX_ID_LOOKUP_SOURCE_LABEL,
  }

  if (!/^\d{8}$/.test(taxId)) {
    return {
      ...base,
      status: 'invalid',
      message: '請先輸入 8 碼統一編號。',
    }
  }

  const companyTitle = syntheticCompanyTitles.get(taxId)
  if (!companyTitle) {
    return {
      ...base,
      status: 'not_found',
      message: 'mock-safe 比對資料找不到此統一編號；live 財政部查詢尚未啟用。',
    }
  }

  return {
    ...base,
    status: 'found',
    companyTitle,
  }
}

export async function lookupGcisMiaolinTaxIdCompanyTitle(
  rawValue: string,
  options: TaxIdLookupOptions = {},
): Promise<MiaolinTaxIdLookupResult> {
  const taxId = normalizeMiaolinTaxIdInput(rawValue)
  const base = {
    taxId,
    providerMode: 'gcis_open_data' as const,
    sourceLabel: GCIS_TAX_ID_LOOKUP_SOURCE_LABEL,
  }

  if (!/^\d{8}$/.test(taxId)) {
    return {
      ...base,
      status: 'invalid',
      message: '請先輸入 8 碼統一編號。',
    }
  }

  const fetcher = options.fetcher ?? globalThis.fetch
  if (!fetcher) {
    return {
      ...base,
      status: 'not_found',
      message: '目前瀏覽器無法呼叫財政部公開資料 API，已清空統編抬頭。',
    }
  }

  try {
    const response = await fetcher(buildGcisTaxIdLookupUrl(taxId), {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: options.signal,
    })
    if (!response.ok) {
      return {
        ...base,
        status: 'not_found',
        message: '財政部公開資料查詢沒有回應，已清空統編抬頭。',
      }
    }

    const payload = await response.json()
    const firstRecord = Array.isArray(payload) ? payload[0] : null
    const companyTitle =
      firstRecord &&
      typeof firstRecord === 'object' &&
      'Company_Name' in firstRecord &&
      typeof firstRecord.Company_Name === 'string'
        ? firstRecord.Company_Name.trim()
        : ''

    if (!companyTitle) {
      return {
        ...base,
        status: 'not_found',
        message: '查無公司名稱，統編抬頭已清空。',
      }
    }

    return {
      ...base,
      status: 'found',
      companyTitle,
    }
  } catch {
    return {
      ...base,
      status: 'not_found',
      message: '財政部公開資料查詢失敗，已清空統編抬頭。',
    }
  }
}

export async function lookupGcisProxyMiaolinTaxIdCompanyTitle(
  rawValue: string,
  options: TaxIdLookupOptions = {},
): Promise<MiaolinTaxIdLookupResult> {
  const taxId = normalizeMiaolinTaxIdInput(rawValue)
  const base = {
    taxId,
    providerMode: 'gcis_open_data' as const,
    sourceLabel: GCIS_TAX_ID_LOOKUP_SOURCE_LABEL,
  }

  if (!/^\d{8}$/.test(taxId)) {
    return {
      ...base,
      status: 'invalid',
      message: '請先輸入 8 碼統一編號。',
    }
  }

  const fetcher = options.fetcher ?? globalThis.fetch
  if (!fetcher) {
    return {
      ...base,
      status: 'not_found',
      message: '目前瀏覽器無法呼叫統編查詢 API，已清空統編抬頭。',
    }
  }

  try {
    const response = await fetcher(buildGcisTaxIdLookupProxyUrl(taxId), {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: options.signal,
    })
    if (!response.ok) {
      return {
        ...base,
        status: 'not_found',
        message: '統編查詢 API 沒有回應，已清空統編抬頭。',
      }
    }

    const payload = await response.json()
    const data =
      payload && typeof payload === 'object' && 'data' in payload ? payload.data : undefined
    const companyTitle =
      data &&
      typeof data === 'object' &&
      'companyName' in data &&
      typeof data.companyName === 'string'
        ? data.companyName.trim()
        : ''

    if (!companyTitle) {
      return {
        ...base,
        status: 'not_found',
        message: '查無公司名稱，統編抬頭已清空。',
      }
    }

    return {
      ...base,
      status: 'found',
      companyTitle,
    }
  } catch {
    return {
      ...base,
      status: 'not_found',
      message: '統編查詢 API 失敗，已清空統編抬頭。',
    }
  }
}

export async function lookupMiaolinTaxIdCompanyTitle(
  rawValue: string,
  providerMode: MiaolinTaxIdLookupProviderMode,
  options: TaxIdLookupOptions = {},
) {
  if (providerMode === 'gcis_open_data') {
    return lookupGcisProxyMiaolinTaxIdCompanyTitle(rawValue, options)
  }

  return lookupSyntheticMiaolinTaxIdCompanyTitle(rawValue)
}
